import {
  Button,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { FileCopyOutlined, FileCopyRounded } from "@material-ui/icons"
import { ViraButton, ViraText } from "@ksana/vira-ui"
import { useEffect, useRef, useState } from "react"
import { ClientFragment } from "../../generated/graphql"
import { useSession } from "../../hooks/useSession"
import {
  approveClientProgressNote,
  approveProgressNote,
  createClientProgressNote,
  fetchCarePlan,
  fetchClientProgressNote,
  fetchProgressNote,
  fetchTranscriptSessionDetail,
  fetchTranscriptSessions,
  generateProgressNote,
  listClientProgressNotes,
  ProgressNoteStatus,
  saveClientProgressNoteDraft,
  saveProgressNoteDraft,
  TranscriptSessionDetail,
} from "../../utils/transcriptApi"
import { buildProgressNoteClientContext } from "../../utils/progressNoteClientContext"
import { colors } from "../../utils/theme"

type NoteFormat = "SOAP" | "BIRP"

interface PostSessionProgressNoteProps {
  client: ClientFragment
  practitionerId: string
  enabled: boolean
  preferredTranscriptSessionId?: string | null
  /** Keep Session Status → Note in sync when the clinician drafts or finalizes. */
  onNoteGateChange?: (gate: {
    status: ProgressNoteStatus | null
    hasContent: boolean
  }) => void
}

type NoteState = Record<NoteFormat, string>
type NoteTimestampState = Partial<Record<NoteFormat, string>>

const EMPTY_NOTES: NoteState = { SOAP: "", BIRP: "" }
const DRAFT_SAVE_DEBOUNCE_MS = 500

export default function PostSessionProgressNote({
  client,
  practitionerId,
  enabled,
  preferredTranscriptSessionId = null,
  onNoteGateChange,
}: PostSessionProgressNoteProps) {
  const classes = useStyles()
  const { session } = useSession()

  const [noteFormat, setNoteFormat] = useState<NoteFormat>("SOAP")
  const [transcriptSession, setTranscriptSession] =
    useState<TranscriptSessionDetail | null>(null)
  const [clientProgressNoteId, setClientProgressNoteId] = useState<
    string | null
  >(null)
  const [transcriptErrorMessage, setTranscriptErrorMessage] = useState<
    string | null
  >(null)
  const [generationErrorMessage, setGenerationErrorMessage] = useState<
    string | null
  >(null)
  const [persistErrorMessage, setPersistErrorMessage] = useState<string | null>(
    null
  )
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false)
  const [isHydratingNote, setIsHydratingNote] = useState(false)
  const [noteHydrated, setNoteHydrated] = useState(false)
  const [isGeneratingNote, setIsGeneratingNote] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [notes, setNotes] = useState<NoteState>(EMPTY_NOTES)
  const [noteStatus, setNoteStatus] = useState<ProgressNoteStatus | null>(null)
  const [noteTimestamps, setNoteTimestamps] = useState<NoteTimestampState>({})
  const [copiedFormat, setCopiedFormat] = useState<NoteFormat | null>(null)
  const [hasPlanItems, setHasPlanItems] = useState(false)
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generationInFlightRef = useRef(false)

  const isFinalized = noteStatus === "signed"
  const activeNote = notes[noteFormat] ?? ""
  const generatedAt = noteTimestamps[noteFormat]
  const selectedClientName =
    client.first_name ||
    client.display_name ||
    client.full_name ||
    "this client"
  const hasAnyNote = Boolean(notes.SOAP.trim() || notes.BIRP.trim())

  useEffect(() => {
    onNoteGateChange?.({
      status: noteStatus,
      hasContent: hasAnyNote,
    })
  }, [hasAnyNote, noteStatus, onNoteGateChange])

  const resetNoteState = () => {
    setNotes(EMPTY_NOTES)
    setClientProgressNoteId(null)
    setNoteTimestamps({})
    setNoteStatus(null)
    setNoteHydrated(false)
    setPersistErrorMessage(null)
    setHasPlanItems(false)
  }

  const hydrateProgressNote = (
    progressNote: {
      id: string | null
      status: ProgressNoteStatus | null
      soapText: string
      birpText: string
    } | null
  ) => {
    setClientProgressNoteId(progressNote?.id ?? null)
    setNotes({
      SOAP: progressNote?.soapText ?? "",
      BIRP: progressNote?.birpText ?? "",
    })
    setNoteStatus(progressNote?.status ?? null)
    const hydratedAt = new Date().toISOString()
    setNoteTimestamps({
      ...(progressNote?.soapText ? { SOAP: hydratedAt } : {}),
      ...(progressNote?.birpText ? { BIRP: hydratedAt } : {}),
    })
    setNoteHydrated(true)
  }

  useEffect(() => {
    if (!enabled || !session?.jwt || !practitionerId) {
      setTranscriptSession(null)
      setTranscriptErrorMessage(null)
      setGenerationErrorMessage(null)
      resetNoteState()
      return
    }

    let ignore = false

    const loadTranscriptSession = async () => {
      try {
        setIsLoadingTranscript(true)
        setIsHydratingNote(true)
        setTranscriptErrorMessage(null)
        setGenerationErrorMessage(null)
        resetNoteState()

        // When embedded in an open session, bind by that id first — do not
        // depend on the sessions list (or its status) to decide we have one.
        let selectedSessionId: string | null =
          preferredTranscriptSessionId ?? null

        if (!selectedSessionId) {
          const sessions = await fetchTranscriptSessions(session.jwt, {
            clientId: client.id,
            practitionerId,
          })
          const latestCompleteByCreatedAt = [...sessions]
            .filter((currentSession) => currentSession.status === "complete")
            .sort(
              (left, right) =>
                new Date(right.createdAt).getTime() -
                new Date(left.createdAt).getTime()
            )[0]
          selectedSessionId = latestCompleteByCreatedAt?.id ?? null
        }

        if (!selectedSessionId) {
          const clientNotes = await listClientProgressNotes(
            session.jwt,
            client.id
          )
          const latestClientNote = clientNotes[0]
          const clientNote = latestClientNote?.id
            ? await fetchClientProgressNote(
                session.jwt,
                client.id,
                latestClientNote.id
              )
            : null

          if (!ignore) {
            setTranscriptSession(null)
            setHasPlanItems(false)
            hydrateProgressNote(clientNote)
            setNoteHydrated(true)
          }
          return
        }

        const detail = await fetchTranscriptSessionDetail(
          session.jwt,
          selectedSessionId
        )

        if (ignore) {
          return
        }

        let planHasItems = false
        try {
          const carePlan = await fetchCarePlan(session.jwt, detail.session.id)
          planHasItems = Boolean(carePlan.plan?.items?.length)
        } catch {
          planHasItems = false
        }
        if (ignore) {
          return
        }

        // Always keep the open session bound — clearing it breaks Generate note.
        setTranscriptSession(detail)
        setHasPlanItems(planHasItems)

        try {
          const progressNote = await fetchProgressNote(
            session.jwt,
            detail.session.id
          )
          if (!ignore) {
            setNotes({
              SOAP: progressNote.soapText,
              BIRP: progressNote.birpText,
            })
            setNoteStatus(progressNote.status)
            const hydratedAt = new Date().toISOString()
            setNoteTimestamps({
              ...(progressNote.soapText ? { SOAP: hydratedAt } : {}),
              ...(progressNote.birpText ? { BIRP: hydratedAt } : {}),
            })
            setNoteHydrated(true)
          }
        } catch {
          if (!ignore) {
            // Note hydrate failure must not drop the selected session.
            setNoteHydrated(true)
          }
        }
      } catch (error) {
        if (!ignore) {
          // Keep preferred session context; don't pretend no session is selected.
          setTranscriptErrorMessage(
            getErrorMessage(
              error,
              "Unable to load session inputs for this note."
            )
          )
          setNoteHydrated(true)
        }
      } finally {
        if (!ignore) {
          setIsLoadingTranscript(false)
          setIsHydratingNote(false)
        }
      }
    }

    void loadTranscriptSession()

    return () => {
      ignore = true
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current)
      }
    }
  }, [
    client.id,
    enabled,
    practitionerId,
    preferredTranscriptSessionId,
    session?.jwt,
  ])

  // Prefer the open session id from the parent — never treat an embedded
  // Progress note tab as "no session selected."
  const boundSessionId =
    preferredTranscriptSessionId ?? transcriptSession?.session.id ?? null
  const hasTranscriptContent =
    progressNoteTranscriptHasContent(transcriptSession)
  const canGenerateFromInputs = hasTranscriptContent || hasPlanItems
  const isGenerateNoteBusy =
    isGeneratingNote || isLoadingTranscript || isHydratingNote
  const canGenerateNote =
    Boolean(boundSessionId) && canGenerateFromInputs && !isGenerateNoteBusy
  const generateNoteDisabledReason =
    isLoadingTranscript || isHydratingNote
      ? "Loading session inputs…"
      : isGeneratingNote
      ? "Generating the progress note…"
      : !boundSessionId
      ? "Unable to bind this note to the open session. Refresh and try again."
      : !canGenerateFromInputs
      ? "Add a recording/transcript or draft an action plan first, then generate the note."
      : ""

  useEffect(() => {
    if (!canGenerateFromInputs) {
      setGenerationErrorMessage(null)
    }
  }, [canGenerateFromInputs])

  const handleGenerateNote = async () => {
    if (!session?.jwt || !boundSessionId || generationInFlightRef.current) {
      return
    }
    if (
      hasAnyNote &&
      !window.confirm(
        "Generating will replace the current progress note. Continue?"
      )
    ) {
      return
    }
    try {
      generationInFlightRef.current = true
      setIsGeneratingNote(true)
      setGenerationErrorMessage(null)

      const progressNote = await generateProgressNote(
        session.jwt,
        boundSessionId,
        noteFormat === "BIRP" ? "birp" : "soap",
        buildProgressNoteClientContext(client)
      )

      setNotes({
        SOAP: progressNote.soapText,
        BIRP: progressNote.birpText,
      })
      setClientProgressNoteId(progressNote.id)
      setNoteStatus(progressNote.status)
      setNoteTimestamps((current) => ({
        ...current,
        [noteFormat]: new Date().toISOString(),
      }))
      setPersistErrorMessage(null)
    } catch (error) {
      setGenerationErrorMessage(
        getErrorMessage(error, "Unable to generate the progress note draft.")
      )
    } finally {
      setIsGeneratingNote(false)
      generationInFlightRef.current = false
    }
  }

  const scheduleDraftSave = (nextNotes: NoteState) => {
    if (!session?.jwt) {
      return
    }

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current)
    }

    draftSaveTimerRef.current = setTimeout(() => {
      void (async () => {
        try {
          setIsSavingDraft(true)
          const saved = transcriptSession
            ? await saveProgressNoteDraft(
                session.jwt,
                transcriptSession.session.id,
                {
                  soapText: nextNotes.SOAP,
                  birpText: nextNotes.BIRP,
                }
              )
            : clientProgressNoteId
            ? await saveClientProgressNoteDraft(
                session.jwt,
                client.id,
                clientProgressNoteId,
                {
                  soapText: nextNotes.SOAP,
                  birpText: nextNotes.BIRP,
                }
              )
            : await createClientProgressNote(session.jwt, client.id, {
                soapText: nextNotes.SOAP,
                birpText: nextNotes.BIRP,
              })
          setClientProgressNoteId(saved.id)
          setNoteStatus(saved.status)
          setPersistErrorMessage(null)
        } catch (error) {
          setPersistErrorMessage(
            getErrorMessage(error, "Unable to save the progress note draft.")
          )
        } finally {
          setIsSavingDraft(false)
        }
      })()
    }, DRAFT_SAVE_DEBOUNCE_MS)
  }

  const handleNoteChange = (value: string) => {
    const nextNotes = { ...notes, [noteFormat]: value }
    setNotes(nextNotes)
    setNoteStatus((current) => current ?? "draft")
    scheduleDraftSave(nextNotes)
  }

  const handleCopyNote = async () => {
    if (!activeNote) {
      return
    }

    await navigator.clipboard.writeText(activeNote)
    setCopiedFormat(noteFormat)
  }

  const handleFinalizeNote = async () => {
    if (!session?.jwt || !hasAnyNote) {
      return
    }

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current)
      draftSaveTimerRef.current = null
    }

    try {
      setIsFinalizing(true)
      setPersistErrorMessage(null)
      let saved
      if (transcriptSession) {
        saved = await approveProgressNote(
          session.jwt,
          transcriptSession.session.id,
          {
            soapText: notes.SOAP,
            birpText: notes.BIRP,
          }
        )
      } else {
        const noteId =
          clientProgressNoteId ??
          (
            await createClientProgressNote(session.jwt, client.id, {
              soapText: notes.SOAP,
              birpText: notes.BIRP,
            })
          ).id
        if (!noteId) {
          throw new Error("Unable to create the progress note.")
        }
        saved = await approveClientProgressNote(
          session.jwt,
          client.id,
          noteId,
          {
            soapText: notes.SOAP,
            birpText: notes.BIRP,
          }
        )
      }
      setClientProgressNoteId(saved.id)
      setNotes({
        SOAP: saved.soapText,
        BIRP: saved.birpText,
      })
      setNoteStatus(saved.status)
    } catch (error) {
      setPersistErrorMessage(
        getErrorMessage(error, "Unable to finalize the progress note.")
      )
    } finally {
      setIsFinalizing(false)
    }
  }

  const title = transcriptSession?.session.createdAt
    ? `Progress Note — Session ${formatSessionDate(
        transcriptSession.session.createdAt
      )}`
    : "Progress Note"
  const draftTimestampLabel = generatedAt
    ? formatGeneratedTime(generatedAt)
    : "Draft"
  const statusChipLabel = isFinalized
    ? isSavingDraft
      ? "Finalized · saving…"
      : "Finalized · editable"
    : isSavingDraft
    ? "Draft · saving…"
    : transcriptSession && generatedAt && hasTranscriptContent
    ? `Draft · from session inputs · ${draftTimestampLabel}`
    : `Draft · ${draftTimestampLabel}`
  const visibleErrorMessage =
    transcriptErrorMessage || generationErrorMessage || persistErrorMessage
  // Always allow manual drafting once hydrated (including empty sessions).
  const showEditor = noteHydrated && !transcriptErrorMessage

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.tabLayout}>
        <ViraText tone="muted" sx={{ fontSize: 14 }}>
          {isFinalized
            ? "This progress note is finalized and saved to the session record. You can still edit it; changes autosave. Generate again to replace the draft from session inputs."
            : "Write the progress note yourself, or generate from available session inputs. Nothing is filed automatically."}
        </ViraText>

        <Tooltip
          title={generateNoteDisabledReason}
          disableHoverListener={!generateNoteDisabledReason}
          disableFocusListener={!generateNoteDisabledReason}
        >
          <span>
            <ViraButton
              variant="primary"
              disabled={!canGenerateNote}
              onClick={() => void handleGenerateNote()}
              sx={{ minHeight: 40 }}
            >
              {isGeneratingNote ? "Generating…" : "Generate note"}
            </ViraButton>
          </span>
        </Tooltip>

        <div className={classes.headerRow}>
          <div className={classes.headerMeta}>
            <Typography variant="body1" className={classes.title}>
              {title}
            </Typography>
            <div
              className={
                isFinalized ? classes.finalizedChip : classes.draftChip
              }
            >
              <Typography
                variant="body2"
                className={
                  isFinalized
                    ? classes.finalizedChipText
                    : classes.draftChipText
                }
              >
                {statusChipLabel}
              </Typography>
            </div>
          </div>
          <div className={classes.segmentedControl}>
            <Button
              className={classes.segmentedButton}
              disableElevation
              onClick={() => setNoteFormat("SOAP")}
              variant={noteFormat === "SOAP" ? "contained" : "text"}
            >
              SOAP
            </Button>
            <Button
              className={classes.segmentedButton}
              disableElevation
              onClick={() => setNoteFormat("BIRP")}
              variant={noteFormat === "BIRP" ? "contained" : "text"}
            >
              BIRP
            </Button>
          </div>
        </div>

        {(isLoadingTranscript || isHydratingNote || isGeneratingNote) &&
          !activeNote && <LinearProgress className={classes.progress} />}

        {visibleErrorMessage ? (
          <div className={classes.errorBanner} role="alert">
            {visibleErrorMessage}
          </div>
        ) : null}

        {showEditor ? (
          <TextField
            className={classes.noteEditor}
            multiline
            fullWidth
            minRows={12}
            variant="outlined"
            value={activeNote}
            placeholder={`Write a ${noteFormat} note for ${selectedClientName}, or generate one from available inputs.`}
            onChange={(event) => handleNoteChange(event.target.value)}
            disabled={isGeneratingNote}
          />
        ) : !isLoadingTranscript && !isHydratingNote ? (
          <Typography variant="body2" className={classes.emptyState}>
            Unable to load the note editor for {selectedClientName}.
          </Typography>
        ) : null}

        <div className={classes.actionsRow}>
          <Button
            className={classes.primaryAction}
            disableElevation
            onClick={() => void handleFinalizeNote()}
            disabled={
              !hasAnyNote ||
              isFinalizing ||
              isGeneratingNote ||
              isLoadingTranscript ||
              (!transcriptSession && !clientProgressNoteId && !hasAnyNote)
            }
          >
            {isFinalizing
              ? "Finalizing…"
              : isFinalized
              ? "Update Finalized Note"
              : "Finalize Note"}
          </Button>
          <Button
            className={classes.primaryAction}
            disableElevation
            onClick={handleCopyNote}
            disabled={!activeNote}
            startIcon={
              copiedFormat === noteFormat ? (
                <FileCopyRounded />
              ) : (
                <FileCopyOutlined />
              )
            }
          >
            {copiedFormat === noteFormat ? "Copied" : "Copy Note"}
          </Button>
        </div>
      </div>
    </Paper>
  )
}

function formatSessionDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatGeneratedTime(value: string): string {
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })
}

/** True when the session has usable transcript text (not an empty manual shell). */
function progressNoteTranscriptHasContent(
  detail: TranscriptSessionDetail | null | undefined
): boolean {
  if (!detail?.transcript) {
    return false
  }
  if (detail.transcript.rawText?.trim()) {
    return true
  }
  return detail.transcript.segments.some((segment) =>
    Boolean(segment.text?.trim())
  )
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : fallback
}

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    padding: 0,
    border: "none",
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  tabLayout: {
    display: "grid",
    gap: theme.spacing(2),
    justifyItems: "start",
    width: "100%",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  headerMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  title: {
    color: "var(--vira-ink, #1E263C)",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.19,
  },
  draftChip: {
    minHeight: 18,
    padding: theme.spacing(0.25, 1.125),
    borderRadius: 16,
    backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
  },
  draftChipText: {
    color: "var(--vira-ink, #1E263C)",
    fontWeight: 700,
    fontSize: 10.5,
    lineHeight: 1.14,
  },
  finalizedChip: {
    minHeight: 18,
    padding: theme.spacing(0.25, 1.125),
    borderRadius: 16,
    backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
  },
  finalizedChipText: {
    color: "var(--vira-ink, #1E263C)",
    fontWeight: 700,
    fontSize: 10.5,
    lineHeight: 1.14,
  },
  segmentedControl: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  segmentedButton: {
    minWidth: 67,
    minHeight: 23,
    padding: theme.spacing(0.25, 2.25),
    borderRadius: 32,
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.23,
    color: "var(--vira-ink, #1E263C)",
    border: `1px solid var(--vira-border, ${colors.primary.cloud})`,
    boxShadow: "none",
    "&.MuiButton-contained": {
      backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
    },
  },
  disclaimer: {
    color: "var(--vira-muted, rgba(30, 38, 60, 0.5))",
    fontSize: 11.5,
    lineHeight: 1.13,
    marginBottom: theme.spacing(3),
  },
  progress: {
    marginBottom: theme.spacing(2),
  },
  noteBody: {
    marginBottom: theme.spacing(4),
  },
  noteEditor: {
    marginBottom: 0,
    width: "100%",
    "& .MuiOutlinedInput-root": {
      alignItems: "flex-start",
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      color: "var(--vira-ink, #1E263C)",
      "& fieldset": {
        borderColor: `var(--vira-border, ${colors.neutral.clay})`,
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "var(--vira-ink, rgba(30, 38, 60, 0.85))",
      fontSize: 13.5,
      lineHeight: "22.95px",
      whiteSpace: "pre-wrap",
    },
  },
  noteParagraph: {
    color: "var(--vira-ink, rgba(30, 38, 60, 0.85))",
    fontSize: 13.5,
    lineHeight: "22.95px",
    marginBottom: theme.spacing(2.5),
    whiteSpace: "pre-wrap",
  },
  noteSectionLabel: {
    fontWeight: 700,
    color: "var(--vira-ink, #1E263C)",
  },
  errorBanner: {
    width: "100%",
    padding: theme.spacing(2),
    borderRadius: 4,
    backgroundColor: colors.secondary.blush,
    color: colors.secondary.crimson,
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 400,
    marginBottom: theme.spacing(3),
  },
  emptyState: {
    color: "var(--vira-muted, #5C6578)",
    marginBottom: theme.spacing(3),
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  primaryAction: {
    minWidth: 109,
    minHeight: 34,
    padding: theme.spacing(1.125, 2.75),
    borderRadius: 32,
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13.5,
    lineHeight: 1.19,
    color: "var(--vira-ink, #2F3B5A)",
    backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
    border: `1px solid var(--vira-border, ${colors.primary.cloud})`,
    boxShadow: "none",
    "&.Mui-disabled": {
      color: "var(--vira-disabled, rgba(47, 59, 90, 0.55))",
      backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
      borderColor: `var(--vira-border, ${colors.primary.cloud})`,
    },
  },
}))
